import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useSubscription } from '../../SubscriptionContext';
import { useUser } from '../../hooks/UserContext';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const Card = ({ children, className }) => (
  <div className={`bg-white p-6 rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

// JST Date Helper
const getJSTDate = (date = new Date()) => {
    return new Date(date.toLocaleString("en-US", {timeZone: "Asia/Tokyo"}));
};

// Billing period calculation logic (JST synchronized)
const getBillingPeriod = (subscriptionStartDateStr) => {
    if (!subscriptionStartDateStr) return null;
    
    // Convert current time to JST for comparison
    const today = getJSTDate();
    today.setHours(0, 0, 0, 0);

    // Convert start date to JST
    const startDate = getJSTDate(new Date(subscriptionStartDateStr));
    const anchorDay = startDate.getDate();

    const getAnchorDateFor = (year, month, day) => {
        const tempDate = new Date(year, month + 1, 0);
        const lastDayOfMonth = tempDate.getDate();
        const validDay = Math.min(day, lastDayOfMonth);
        return new Date(year, month, validDay);
    };

    const currentMonthAnchor = getAnchorDateFor(today.getFullYear(), today.getMonth(), anchorDay);

    let periodStart;
    if (today.getTime() >= currentMonthAnchor.getTime()) {
        periodStart = currentMonthAnchor;
    } else {
        let prevMonth = today.getMonth() - 1;
        let prevMonthYear = today.getFullYear();
        if (prevMonth < 0) {
            prevMonth = 11;
            prevMonthYear--;
        }
        periodStart = getAnchorDateFor(prevMonthYear, prevMonth, anchorDay);
    }

    let nextPeriodStartMonth = periodStart.getMonth() + 1;
    let nextPeriodStartYear = periodStart.getFullYear();
    if (nextPeriodStartMonth > 11) {
        nextPeriodStartMonth = 0;
        nextPeriodStartYear++;
    }
    
    const nextPeriodStartDate = getAnchorDateFor(nextPeriodStartYear, nextPeriodStartMonth, anchorDay);
    const periodEnd = new Date(nextPeriodStartDate);
    periodEnd.setDate(periodEnd.getDate() - 1);

    return { periodStart, periodEnd };
};

const formatDateToDocId = (date) => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const MonthlyChart = () => {
  const { t } = useTranslation('home');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  const { subscriptionStartDate } = useSubscription();
  const { user } = useUser();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchChartData = async () => {
      if (!subscriptionStartDate || !user?.billwaveCustomerId) return;

      try {
        setLoading(true);

        const period = getBillingPeriod(subscriptionStartDate);
        if (!period) throw new Error("期間の計算に失敗しました");

        // 1. Initialize data for the specific billing period (JST based)
        const fullPeriodData = [];
        let currentDate = new Date(period.periodStart);
        while (currentDate <= period.periodEnd) {
            fullPeriodData.push({
                dateId: formatDateToDocId(currentDate),
                displayDate: `${currentDate.getMonth() + 1}/${currentDate.getDate()}`,
                success: 0,
                failed: 0,
            });
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        const dataMap = new Map(fullPeriodData.map(item => [item.dateId, item]));

        // 2. Fetch data from /billwaveAccounts/{id}/dashboard_stats
        const statsRef = collection(db, 'billwaveAccounts', user.billwaveCustomerId, 'dashboard_stats');
        const q = query(
          statsRef,
          where('date', '>=', formatDateToDocId(period.periodStart)),
          where('date', '<=', formatDateToDocId(period.periodEnd)),
          orderBy('date', 'asc')
        );

        const querySnapshot = await getDocs(q);

        // 3. Merge Firestore data
        if (!querySnapshot.empty) {
            querySnapshot.docs.forEach(doc => {
                const docData = doc.data();
                if (dataMap.has(docData.date)) {
                    const existingData = dataMap.get(docData.date);
                    dataMap.set(docData.date, {
                        ...existingData,
                        success: docData.invoice_success || 0,
                        failed: docData.invoice_failed || 0,
                    });
                }
            });
        }

        setChartData(Array.from(dataMap.values()));
        setError(null);
      } catch (err) {
        console.error("Error fetching chart data:", err);
        setError('グラフデータの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [subscriptionStartDate, user]);

  return (
    <Card>
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-bold text-gray-800">{t('chart.title', '今月の請求処理状況')}</h3>
        {error && <span className="text-red-500 text-sm">{error}</span>}
      </div>
      <div style={{ width: '100%', height: 300 }}>
        {loading ? (
            <div className="text-center text-gray-500" style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>読み込み中...</div>
        ) : (
            <ResponsiveContainer width="100%" height={300}>
                <BarChart
                    data={chartData}
                    margin={{ top: 5, right: 20, left: 20, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                        dataKey="displayDate" 
                        tick={{ fontSize: 10 }} 
                        axisLine={false} 
                        tickLine={false} 
                        interval={isMobile ? 3 : 0}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} label={{ value: t('chart.yAxis', '件数'), angle: -90, position: 'insideLeft', offset: -5, fontSize: 12 }}/>
                    <Tooltip
                        contentStyle={{ borderRadius: '0.5rem', borderColor: '#e5e7eb', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}
                        formatter={(value, name) => [`${value}${t('chart.unit', '件')}`, name]}
                    />
                    <Legend 
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ fontSize: '14px' }}
                    />
                    <Bar dataKey="success" stackId="a" fill="#34d399" name={t('chart.success', '成功')}/>
                    <Bar dataKey="failed" stackId="a" fill="#ef4444" name={t('chart.failed', '失敗')}/>
                </BarChart>
            </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default MonthlyChart;
